import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  Grid,
  Paper,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CustomButton from "../../../../components/common/button/CustomButton";
import "../../storageComponent/StorageComponent.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSitesAction,
  setAddSiteResponse,
} from "../../../../store/reducers/storageSettingSlice";
import { Site } from "./site.type";

interface SiteListProps {
  storages: Site[];
  selectedSiteId?: any;
  // onSelectStorage: (storageId: string) => void;
  setSelectedSiteId?: any;
  onAddNew: () => void;
  filterType: "all" | "available";
  setFilterType: (type: "all" | "available") => void;
}

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
}

const ROW_HEIGHT = 50; // px, adjust to your row/card height
const MIN_LIST_LIMIT = 10;

const SiteList: React.FC<SiteListProps> = ({
  storages,
  selectedSiteId,
  setSelectedSiteId,
  onAddNew,
  filterType,
  setFilterType,
}) => {
  const dispatch = useDispatch();
  const { isAddSiteSuccess } = useSelector(
    (state: any) => state.storageSetting,
  );

  const getResponsiveLimit = () => {
    if (typeof window === "undefined") {
      return 10;
    }

    return Math.max(
      MIN_LIST_LIMIT,
      Math.floor(window.innerHeight / ROW_HEIGHT),
    );
  };

  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: getResponsiveLimit(),
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
  });

 useEffect(() => {
  dispatch(getAllSitesAction(filterOption));
}, [filterOption]);

useEffect(() => {
  if (isAddSiteSuccess) {
    dispatch(getAllSitesAction(filterOption));
    dispatch(setAddSiteResponse(false));
  }
}, [isAddSiteSuccess]);

  return (
    <Box className="storage-list-container">
      {/* Header Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          marginBottom: "10px",
          justifyContent: "space-between",
        }}
      >
        <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="Add New"
          onClick={onAddNew}
        />
        <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="Filter"
          variant="outlined"
          onClick={() => {}}
        />
      </Box>

      {/* Filter Tabs */}
      <Box
        sx={{
          background: "#fff",
          fontSize: "12px",
          fontWeight: 500,
          marginBottom: "10px",
          minHeight: "12px",
          padding: "14px 7px",
          border: "1px solid #C4CDD5",
          height: "32px",
          display: "flex",
          gap: "8px",
        }}
      >
        <Button
          fullWidth
          onClick={() => setFilterType("all")}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            margin: "0 4px",
            minHeight: "12px",
            height: "32px",
            backgroundColor: filterType === "all" ? "#02906f" : "#f0f0f0",
            color: filterType === "all" ? "#fff" : "inherit",
            fontWeight: filterType === "all" ? 600 : 400,
            fontSize: "12px",
            "&:hover": {
              backgroundColor: filterType === "all" ? "#02906f" : "#e0e0e0",
            },
          }}
        >
          All ({storages?.length})
        </Button>
        <Button
          fullWidth
          onClick={() => setFilterType("available")}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            margin: "0 4px",
            minHeight: "12px",
            height: "32px",
            backgroundColor: filterType === "available" ? "#02906f" : "#f0f0f0",
            color: filterType === "available" ? "#fff" : "inherit",
            fontWeight: filterType === "available" ? 600 : 400,
            fontSize: "12px",
            "&:hover": {
              backgroundColor:
                filterType === "available" ? "#02906f" : "#e0e0e0",
            },
          }}
        >
          Available ({storages?.length})
        </Button>
      </Box>

      {/* Search Box */}
      <TextField
        fullWidth
        placeholder="Search by Storage name"
        value={filterOption?.search}
        onChange={(e) => {
          setFilterOption({
            ...filterOption,
            search: e.target.value,
          });
        }}
        size="small"
        sx={{
          marginBottom: "10px",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
            // fontSize: "12px",
          },
          "& input": {
            padding: "4px 10px",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#637381", fontSize: "18px" }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Table Header */}
      <Grid
        container
        spacing={0}
        alignItems="center"
        sx={{
          background: "#E5F4F1",
          color: "#637381",
          padding: "10px 10px",
          marginBottom: "4px",
          border: "1px solid #C4CDD5",
        }}
      >
        <Grid item xs={5} sx={{ borderRight: "1px solid #C4CDD5" }}>
          <Typography variant="body2" fontWeight={600}>
            Site Name
          </Typography>
        </Grid>
        <Grid
          item
          xs={4}
          sx={{ borderRight: "1px solid #C4CDD5", textAlign: "center" }}
        >
          <Typography variant="body2" fontWeight={600}>
            Site Type
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ textAlign: "center" }}>
          <Typography variant="body2" fontWeight={600}>
            Status
          </Typography>
        </Grid>
      </Grid>

      {/* Storage List */}
      <Box sx={{ paddingBottom: 2 }}>
        {storages.length > 0 ? (
          storages.map((item: any) => (
            <Paper
              key={item.id}
              elevation={1}
              sx={{
                border: `1px solid ${
                  selectedSiteId === item.id
                    ? item.completedUnits === item.totalUnits
                      ? "#008e73"
                      : "#02906f"
                    : "#ddd"
                }`,
                display: "flex",
                alignItems: "center",
                p: "2px",
                backgroundColor: `${
                  selectedSiteId === item.id
                    ? item.completedUnits === item.totalUnits
                      ? alpha("#008e73", 0.15)
                      : alpha("#02906f", 0.15)
                    : "#fff"
                }`,
                position: "relative",
                overflow: "hidden",
                marginBottom: "4px",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedSiteId(item.id);
              }}
            >
              {/* Left Accent */}
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 10,
                  backgroundColor:
                    item.completedUnits === item.totalUnits
                      ? "#008e73"
                      : "#02906f",
                }}
              />

              {/* Content */}
              <Box sx={{ ml: 2, width: "100%" }}>
                <Grid container spacing={0}>
                  <Grid item xs={5}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ lineHeight: 1.5, textTransform: "capitalize" }}
                    >
                      {item.site_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.postcode}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">{item.site_type}</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {item?.status}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          ))
        ) : (
          <Box
            sx={{
              padding: "40px 20px",
              textAlign: "center",
              color: "#999",
            }}
          >
            <Typography>No storages found</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SiteList;
